/**
 * mock.ts (features/policies) — DMS M12 fixtures for the Policies-page
 * "See history" control + the policy-detail "Dokumente öffnen" trigger.
 *
 * `POLICY_VERSIONS_MOCK` is keyed by policy id, ordered `version_nr` ASC
 * (mirrors the real `GET /policies/:id/versions` ordering — see
 * `apiClient.policies.versions`'s own doc comment). Only `p1` (POL-339-2266,
 * Basler Chemie AG — the id every existing PoliceDetailScreen/PoliciesScreen
 * test already renders) carries a two-version chain; every other mock policy
 * resolves to an empty array (current-only, nothing for "See history" to
 * reveal — the honest default).
 *
 * The two versions deliberately reuse the SAME `source_document_id` pair the
 * Ablage grid mock already ships (`doc-swi-0002603` / `doc-swi-0002603-v1`,
 * features/documents/mock.ts#DOCUMENTS_MOCK) so "Öffnen" on an older version
 * lands on a document `DocumentViewerScreen`'s mock actually resolves —
 * end-to-end clickable in mock mode, not just visually present.
 *
 * [Source: apps/web/src/lib/api-client.ts#policies.versions]
 * [Source: apps/web/src/features/documents/mock.ts#DOCUMENTS_MOCK]
 * [Source: projects/broker-ag/dms/spec.md §4.3, §M12]
 */
import type { PolicyVersionDto } from '@brokly/shared';

export const POLICY_VERSIONS_MOCK: Record<string, PolicyVersionDto[]> = {
  p1: [
    {
      id: 'pv-p1-v1',
      policy_id: 'p1',
      version_nr: 1,
      valid_from: '2024-01-01T00:00:00.000Z',
      valid_to: '2026-01-01T00:00:00.000Z',
      premium_net: '44500.00',
      premium_gross: '46700.00',
      stamp_duty: '2100.00',
      fees: null,
      coverage_json: null,
      coverage_delta_json: null,
      change_reason: null,
      source_document_id: 'doc-swi-0002603-v1',
      created_at: '2024-01-01T09:00:00.000Z',
    },
    {
      id: 'pv-p1-v2',
      policy_id: 'p1',
      version_nr: 2,
      valid_from: '2026-01-01T00:00:00.000Z',
      valid_to: null,
      premium_net: '46000.00',
      premium_gross: '48200.00',
      stamp_duty: '2200.00',
      fees: null,
      coverage_json: null,
      coverage_delta_json: null,
      change_reason: 'Prämienanpassung Renewal 2026',
      source_document_id: 'doc-swi-0002603',
      created_at: '2026-01-01T09:00:00.000Z',
    },
  ],
};

/** Version chain for a policy id (version_nr ASC), empty when none is mocked. */
export function policyVersionsFor(policyId: string): PolicyVersionDto[] {
  return POLICY_VERSIONS_MOCK[policyId] ?? [];
}
